import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {EventService} from "../../core/services/event.service";
import {EditGoalDialogComponent} from "../../core/components/edit-goal-dialog/edit-goal-dialog.component";
import {UiStateService} from "../../core/services/ui-state.service";
import {ActivatedRoute, Router} from "@angular/router";
import {EditTeamComponent} from "../../core/components/dialogs/edit-team/edit-team.component";
import { PageService } from '../../core/services/page.service';
import { DonationService } from '../../core/services/donation.service';
import { TeamService } from '../../core/services/team.service';
import {ToastrService} from "ngx-toastr";
import {CurrencyPipe} from '@angular/common'
import {ProgressService} from "../../core/services/progress.service";
import {HybridToolsService} from "../../core/services/hybrid-tools.service";
import { DatagridComponent } from '../../ui/datagrid/datagrid.component';
import PullToRefresh from 'pulltorefreshjs';

declare var moment: any;

@Component({
    selector: 'app-teams',
    templateUrl: './teams.component.html',
    styleUrls: ['./teams.component.scss']
})
export class TeamsPageComponent implements AfterViewInit, OnInit, OnDestroy {
    @ViewChild('teamTable') datagrid: DatagridComponent;

    public team = null;
    teamLabel: string;
    listView = false;
    canEditGoal: boolean;
    teamGridOptions: any;
    isWidgetVisible: boolean;

    constructor(private router: Router,
                public modalService: NgbModal,
                public eventService: EventService,
                public pageService: PageService,
                public uiStateService: UiStateService,
                public donationService: DonationService,
                public hybridToolsService: HybridToolsService,
                private teamService: TeamService,
                private route: ActivatedRoute,
                private ToastrService: ToastrService,
                private cp: CurrencyPipe,
                private progressService: ProgressService,
                private cd: ChangeDetectorRef) {
        this.eventService.onEventInit.subscribe(event => {
            this.checkCanEditGoal();
            this.checkIsWidgetVisible();
            this.setupGrid();
        });
        this.eventService.onEventChange.subscribe(event => {
            this.redirectToHomeIfNoTeam();
            this.checkCanEditGoal();
            this.checkIsWidgetVisible();
            this.setupGrid();
        });
    }

    ngOnInit() {
        this.redirectToHomeIfNoTeam();
        this.checkCanEditGoal();
        this.checkIsWidgetVisible();
        this.setupGrid();
    }

    ngAfterViewInit(): void {
        if (PullToRefresh) {
            PullToRefresh.init({
                mainElement: 'app-teams', // above which element?
                onRefresh: (done) => {
                    this.eventService.reloadTeams().subscribe(() => {
                        this.setupGrid();
                        done();
                    });
                }
            });
        }
    }

    ngOnDestroy() {
        if (PullToRefresh) {
            PullToRefresh.destroyAll();
        }
    }

    setupGrid() {
        let teamData: any;
        try {
            teamData = this.eventService.selectedEvent.gql.Participant[0].team.teamMembers;
        } catch (err) {
            // Team data isn't there.
        }
        let columns: any;

        columns = [
            {
                title: 'Name',
                data: 'firstName',
                responsivePriority: 1,
                render: (data, type, row) => {
                    const lastName = row.lastName ? row.lastName : '';
                    return `<a class="team-name" href="javascript:void(0)">` + row.firstName + ' ' + lastName + '</a>';
                }
            },
            {title: 'Email', data: 'email'},
            {
                title: 'Phone',
                data: 'phoneNumber',
                render: (data, type, row) => {
                    if (row.phoneNumber) {
                        return (row.areaCode ? '(' + row.areaCode + ')' : '') + ' ' + row.phoneNumber;
                    } else  {
                        return '';
                    }
                }
            },
            {title: 'In Honor Of', data: 'honoree'},
            {
                title: 'Funds from LLS Fundraising Page',
                data: 'totalFunds',
                type: 'currency',
                render: (data, type, row) => {
                    if (this.isWidgetVisible || this.isCurrentUserWidget(row)) {
                        return this.cp.transform(row.totalFunds, 'USD', true);
                    } else  {
                        return '';
                    }
                }
            },
            {
                title: 'Facebook Funds',
                data: 'facebookFunds',
                type: 'currency',
                render: (data, type, row) => {
                    if (this.isWidgetVisible || this.isCurrentUserWidget(row)) {
                        return this.cp.transform(row.facebookFunds, 'USD', true);
                    } else  {
                        return '';
                    }
                }

            },
            {
                title: 'Promises',
                data: 'outstandingMatchingGifts',
                type: 'currency',
                render: (data, type, row) => {
                    if (this.isWidgetVisible || this.isCurrentUserWidget(row)) {
                        return this.cp.transform(row.outstandingMatchingGifts, 'USD', true);
                    } else  {
                        return '';
                    }
                }

            },
            {
                title: 'Sponsorships',
                data: 'outstandingSponsorships',
                type: 'currency',
                render: (data, type, row) => {
                    if (this.isWidgetVisible || this.isCurrentUserWidget(row)) {
                        return this.cp.transform(row.outstandingSponsorships, 'USD', true);
                    } else  {
                        return '';
                    }
                }

            },
            {
                title: 'Raised',
                data: 'totalFunds',
                type: 'currency',
                responsivePriority: 1,
                render: (data, type, row) => {
                    if (this.isWidgetVisible || this.isCurrentUserWidget(row)) {
                        return this.cp.transform(row.grandTotal, 'USD', true);
                    } else  {
                        return '';
                    }
                }
            },
            {
                title: 'Registration Status',
                data: 'registrationStatusTypeDescription',
                responsivePriority: 1,
                render: (data, type, row) => {
                    if (row.registrationStatusTypeDescription && (this.isWidgetVisible || this.isCurrentUserWidget(row))) {
                        return row.registrationStatusTypeDescription;
                    } else  {
                        return '';
                    }
                }
            },
            {
                title: 'Sport/Course',
                data: 'sportCourse',
                responsivePriority: 1,
                render: (data, type, row) => {
                    if (this.isWidgetVisible || this.isCurrentUserWidget(row)) {
                        return row.sportCourse;
                    } else  {
                        return '';
                    }
                }
            },
            {
                title: 'Fundraising Goal',
                data: 'fundraisingGoal',
                type: 'currency',
                responsivePriority: 1,
                render: (data, type, row) => {
                    if ((this.isWidgetVisible || this.isCurrentUserWidget(row)) && row.fundraisingGoal > 0) {
                        return this.cp.transform(row.fundraisingGoal, 'USD', true);
                    } else  {
                        return '';
                    }
                }
            },
            {
                title: 'Market',
                data: 'officeName',
                responsivePriority: 1,
                render: (data, type, row) => {
                    if (this.isWidgetVisible || this.isCurrentUserWidget(row)) {
                        return row.officeName;
                    } else  {
                        return '';
                    }
                }
            },
            {
                title: 'Registration Date',
                data: 'regDate',
                responsivePriority: 1,
                render: (data, type, row) => {
                    if (this.isWidgetVisible || this.isCurrentUserWidget(row)) {
                        return moment.utc(row.regDate).format('MM/DD/YYYY');
                    } else  {
                        return '';
                    }
                }
            },
            {
                title: 'Event Name',
                data: 'eventName',
                responsivePriority: 1,
                render: (data, type, row) => {
                    if (this.isWidgetVisible || this.isCurrentUserWidget(row)) {
                        return row.eventName;
                    } else  {
                        return '';
                    }
                }
            },
            {
                title: 'Event Date',
                data: 'eventDate',
                responsivePriority: 1,
                render: (data, type, row) => {
                    if (this.isWidgetVisible || this.isCurrentUserWidget(row)) {
                        return moment.utc(row.eventDate).format('MM/DD/YYYY');
                    } else  {
                        return '';
                    }
                }
            },
            {
                title: 'Recommitted',
                data: 'recommitted',
                responsivePriority: 1,
                render: (data, type, row) => {
                    if ((this.isWidgetVisible || this.isCurrentUserWidget(row)) && row.recommitted != null) {
                        return row.recommitted ? 'Yes' : 'No';
                    } else  {
                        return '';
                    }
                }
            },
            {
                title: 'Fundraising Commitment',
                data: 'fundraisingCommitment',
                type: 'currency',
                responsivePriority: 1,
                render: (data, type, row) => {
                    if ((this.isWidgetVisible || this.isCurrentUserWidget(row)) && row.fundraisingCommitment != null) {
                        return this.cp.transform(row.fundraisingCommitment, 'USD', true);
                    } else  {
                        return '';
                    }
                }
            }
        ];

        this.teamGridOptions = {
            data: teamData,
            columns: columns,
            initComplete: function() {
                // Nothing needed here yet.
            }
        };

    }

    isCurrentUserWidget(member) {
        let currentUserPrimaryEmail = this.extractCurrentUserPrimaryEmail();
        if(!!currentUserPrimaryEmail) {
            return currentUserPrimaryEmail.email === member.email;
        }
        return false;
    }

    extractCurrentUserPrimaryEmail() {
        let constituent = this.eventService.selectedEvent.gql.Participant[0].individualConstituent;
        return constituent.constituentEmails.find(email => {
            if(!!email.primaryFl)  {
                return true;
            }
        });
    }

    enableListView(onoff: boolean) {
        this.listView = onoff;
        this.cd.detectChanges();
        if (this.listView) {
            this.datagrid.bindClick('.team-name', (event) => {
                var tr = $(event.target).closest('tr');
                var row = this.datagrid.dtInstance.row(tr);
                this.hybridToolsService.openExternalLink(row.data().fullUrl);
            });
        }
    }

    checkIsWidgetVisible() {
        const role = this.eventService.selectedEvent.gql.Participant[0].teamRoleType;
        if(this.eventService.selectedEvent.gql.Participant[0].team && (this.eventService.selectedEvent.cssClass === 'program-soy' || this.eventService.selectedEvent.cssClass === 'program-svoy') && role.name === 'CAMPAIGN_TEAM_CAPTAIN') {
            this.isWidgetVisible = true;
        } else if (this.eventService.selectedEvent.gql.Participant[0].team &&
            this.eventService.selectedEvent.programGroupName === 'MWOY' &&
            (role.name !== 'CANDIDATE' && role.name !== 'HONORED_CANDIDATE_F' && role.name !== 'MWOY_ALL_STAR')) {
            this.isWidgetVisible = false;
        } else {
            this.isWidgetVisible = this.eventService.selectedEvent.gql.Participant[0].team;
        }
    }

    openChangeGoalModal() {
        this.modalService.open(EditGoalDialogComponent);
    }

    openchangeTeamModal() {
        let modal = this.modalService.open(EditTeamComponent);
        modal.result.then(result => {
            if (result) {
                this.progressService.startProgress(false);
                this.teamService.saveTeamEdit(this.eventService.selectedEvent.gql.Participant[0].team.teamId, {
                    name: this.eventService.selectedEvent.team.name,
                    imageID: result.constituentImageLibraryId
                }).subscribe(() => {
                    this.ToastrService.success("Team updated successfully.");
                    var teamFormMembers = this.findTeamInTeamMembers(this.eventService.selectedEvent.gql.Participant[0].team.teamId);
                    if(teamFormMembers) {
                        teamFormMembers.imagePath = result.imagePath;
                    }
                    this.progressService.stopProgress(false);
                }, err => {
                    this.ToastrService.error("Error happened during update.");
                    this.progressService.stopProgress(false);
                });
                this.eventService.selectedEvent.team.imagePath = result.imagePath;
            }
        }, () => {/* avoids error on 'cancel' */});
    }

    findTeamInTeamMembers(teamId) {
        return this.eventService.selectedEvent.gql.Participant[0].team.teamMembers.find(function(teamMember) {
            return teamMember.teamMemberId === teamId * -1;
        });
    }

    checkCanEditGoal() {
        const program = this.eventService.selectedEvent.cssClass;
        const role = this.eventService.selectedEvent.gql.Participant[0].teamRoleType;
        if ((!this.eventService.selectedEvent.isPastEvent && role &&
                (role.name === 'CAPTAIN' || role.name === 'CANDIDATE'
                    || role.name === 'HONORED_CANDIDATE_F' || role.name === 'MWOY_ALL_STAR')) ||
            (!this.eventService.selectedEvent.isPastEvent && program === 'program-regatta')) {
            this.canEditGoal = true;
        } else {
            this.canEditGoal = false;
        }
    }

    redirectToHomeIfNoTeam() {
        if (this.eventService.selectedEvent == null || !this.eventService.selectedEvent.gql.Participant[0].team) {
            // go home
            this.router.navigate(['/home']);
            return true;
        }
    }
}
